import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// A flash dot appears near the edges of the play area. Tap it as fast as possible.
// 15 rounds. Score = Math.max(1, 1500 − avgMs). Higher = faster = better.
const POSITIONS = [
  { x: 5,  y: 5  }, { x: 50, y: 5  }, { x: 92, y: 5  },
  { x: 5,  y: 50 }, { x: 92, y: 50 },
  { x: 5,  y: 92 }, { x: 50, y: 92 }, { x: 92, y: 92 },
  { x: 20, y: 15 }, { x: 80, y: 15 }, { x: 20, y: 82 }, { x: 80, y: 82 },
];

function PeripheralFlashGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const ROUNDS = 15;
  const [state, setState]           = useState('idle'); // idle|wait|go|result
  const [dotPos, setDotPos]         = useState(null);
  const [round, setRound]           = useState(0);
  const [times, setTimes]           = useState([]);
  const [finished, setFinished]     = useState(false);
  const [lastMs, setLastMs]         = useState(null);
  const [best, setBest]             = useStorage('gm_periphflash_best', null);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]       = useState(null);
  const [showResults, setShowResults] = useState(null);

  // Auto-set partner when arriving from game room invite
  useEffect(() => {
    if (invitePartner) { setPartner(invitePartner); }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRef  = useRef(null);
  const timerRef  = useRef(null);
  const timesRef  = useRef([]);

  const showFlash = () => {
    const pos = POSITIONS[Math.floor(Math.random() * POSITIONS.length)];
    setDotPos(pos);
    setState('go');
    startRef.current = Date.now();
    // auto-expire after 1.2s (counts as miss — no penalty, just slow)
    timerRef.current = setTimeout(() => {
      timesRef.current = [...timesRef.current, 1200];
      setTimes(t => [...t, 1200]);
      proceedNext();
    }, 1200);
  };

  const proceedNext = () => {
    setDotPos(null);
    const nextR = round + 1;
    setRound(nextR);
    if (nextR > ROUNDS) { setTimeout(() => setFinished(true), 400); }
    else { setState('wait'); timerRef.current = setTimeout(showFlash, 600 + Math.random() * 1000); }
  };

  const start = () => {
    timesRef.current = [];
    setTimes([]); setRound(1); setLastMs(null);
    setFinished(false); setShowResults(null);
    setState('wait');
    timerRef.current = setTimeout(showFlash, 800 + Math.random() * 1000);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (finished) {
      const avg = timesRef.current.length ? Math.round(timesRef.current.reduce((a, b) => a + b, 0) / timesRef.current.length) : 1200;
      const stored = Math.max(1, 1500 - avg);
      if (!best || stored > best) setBest(stored);
      saveGameScore('peripheralflash', stored);
      if (partner) setShowResults({ myScore: avg, partnerScore: Math.floor(Math.random() * 200) + 300 });
    }
  }, [finished]); // eslint-disable-line

  const tapDot = (e) => {
    if (state !== 'go') return;
    e.stopPropagation();
    clearTimeout(timerRef.current);
    const ms = Date.now() - startRef.current;
    timesRef.current = [...timesRef.current, ms];
    setTimes(t => [...t, ms]);
    setLastMs(ms);
    proceedNext();
  };

  const avgMs = times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
  const bestDisplay = best ? `${Math.max(0, 1500 - best)}ms` : '–';

  if (showResults) return <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel="ms avg" partnerId={partner?.id} partnerScore={showResults.partnerScore} partnerLabel="ms avg" aiComment={nl ? `Gemiddelde van ${showResults.myScore}ms! Perifeer Flash traint je perifeer zicht en reactievermogen.` : `Average of ${showResults.myScore}ms! Peripheral Flash trains your peripheral vision and reaction speed.`} onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start} />;

  return (
    <div>
      {showInvite && <MultiplayerInviteModal lang={lang} title="Peripheral Flash" icon="👁" allUsers={allUsers} friends={friends} onInvite={(user) => { setPartner(user); setShowInvite(false); }} onClose={() => setShowInvite(false)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>
      {partner && <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}><Avatar user={partner} size={26} /><span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span></div>}

      {state === 'idle' && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>👁</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Perifeer Signaal' : 'Peripheral Flash'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.6 }}>
            {nl ? `Een lichtflits verschijnt aan de randen van het scherm. Tik het zo snel mogelijk! ${ROUNDS} rondes.` : `A flash appears near the edges of the screen. Tap it as fast as possible! ${ROUNDS} rounds.`}
          </div>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 12 }), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>BEST</div><div style={{ fontSize: 18, color: C.teal, fontWeight: 700 }}>{bestDisplay}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDES' : 'ROUNDS'}</div><div style={{ fontSize: 18, color: _T.accent, fontWeight: 700 }}>{ROUNDS}</div></div>
          </div>
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {(state === 'wait' || state === 'go') && !finished && (
        <>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 10 }), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDE' : 'ROUND'}</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{round}/{ROUNDS}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>AVG</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{avgMs ? `${avgMs}ms` : '–'}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>LAST</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{lastMs ? `${lastMs}ms` : '–'}</div></div>
          </div>

          <div style={{ position: 'relative', width: '100%', height: 320, background: _T.bg3, borderRadius: 18, border: `1px solid ${_T.cardBorderBot}`, overflow: 'hidden', marginBottom: 10 }}>
            {state === 'wait' && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                <div style={{ fontSize: 36 }}>👁</div>
                <div style={{ color: C.silver, fontSize: 12, marginTop: 6 }}>{nl ? 'Let op de randen...' : 'Watch the edges...'}</div>
              </div>
            )}
            {dotPos && state === 'go' && (
              <div onClick={tapDot} className="popIn" style={{ position: 'absolute', left: `${dotPos.x}%`, top: `${dotPos.y}%`, width: 52, height: 52, borderRadius: '50%', background: '#C8A84B', border: '3px solid #C8A84B88', cursor: 'pointer', transform: 'translate(-50%,-50%)', boxShadow: '0 0 24px #C8A84B88', zIndex: 5 }} />
            )}
          </div>
          <div style={{ textAlign: 'center', fontSize: 11, color: C.silver }}>{nl ? 'Tik de gele cirkel zodra hij verschijnt' : 'Tap the yellow circle the moment it appears'}</div>
        </>
      )}

      {finished && !showResults && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700, marginBottom: 4 }}>{avgMs ? `${avgMs}ms` : '–'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{nl ? 'gemiddelde reactietijd' : 'average reaction time'}</div>
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default PeripheralFlashGame;
